import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PButton, PHeading, PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { DocumentResponse } from "@slfinrtl/ui-bff-routes"
import { generateExposeRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useState } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { commonMessages } from "src/common/messages/common.messages"
import { openPdf } from "src/common/pdf/openPdf"
import { ButtonGroup, Checkbox, FormLayout, RadioGroup, ValidationMessage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { useBffDynamic } from "src/hooks/useBffDynamic/useBffDynamic"
import { useConfig } from "src/hooks/useConfig/useConfig"
import { useLanguageOptions } from "src/hooks/useLanguageOptions/useLanguageOptions"

const messages = defineMessages({
	headline: {
		defaultMessage: "Print Exposé",
		id: "printExpose.headline",
	},
	languageHeadline: {
		defaultMessage: "Choose Language",
		id: "printExpose.language.headline",
	},
	languageSubheadline: {
		defaultMessage: "Choose the language in which you want to print the exposé.",
		id: "printExpose.language.subheadline",
	},
	withFsOffer: {
		defaultMessage: "Print Financial Offer",
		id: "printExpose.financialOffer.label",
	},
})

export interface PrintExposeModalContainerProps {
	vehicleKey: string
}

type Props = ModalProps<PrintExposeModalContainerProps>

export const PrintExposeModalContainer = withErrorHandler(({ id, modalProps }: Props) => {
	const { vehicleKey } = modalProps
	const { hideModal } = useContext(ModalContext)
	const [errorMessage, setErrorMessage] = useState<string | undefined>()

	const { formatMessage } = useIntl()
	const { availableLocales, locale } = useDealerContext()
	const printExpose = useBffDynamic(generateExposeRoute, "inline")
	const {
		print: {
			financialServices: { exposeFsOfferEnabled },
		},
	} = useConfig()

	const hide = useCallback(() => hideModal(id), [hideModal, id])

	const [isSelectedFinancialOffer, setIsSelectedFinancialOffer] = useState(exposeFsOfferEnabled ? true : undefined)
	const [selectedLocale, setSelectedLocale] = useState<Locale>(
		availableLocales.find((item) => item === locale) ?? availableLocales[0],
	)
	const [isPrinting, setIsPrinting] = useState(false)

	const handlePrintSuccess = useCallback(
		({ document }: DocumentResponse) => {
			openPdf(document)
			hide()
		},
		[hide],
	)

	const handleError = useCallback(
		(error: any) => {
			setErrorMessage(error?.response?.data?.message ?? error.message ?? formatMessage(formMessages.errorGeneral))
		},
		[formatMessage],
	)

	const onSubmit = useCallback(() => {
		if (!selectedLocale) {
			return
		}

		setErrorMessage(undefined)
		setIsPrinting(true)
		printExpose({
			body: undefined,
			pathParams: {
				inventoryId: vehicleKey,
				languageTag: selectedLocale,
			},
			queryParams: {
				withFsOffer: isSelectedFinancialOffer ? "true" : "false",
			},
		})
			.then(handlePrintSuccess)
			.catch(handleError)
			.finally(() => setIsPrinting(false))
	}, [selectedLocale, handlePrintSuccess, vehicleKey, printExpose, handleError, isSelectedFinancialOffer])

	const languageOptions = useLanguageOptions()

	const handleLanguageChanged = useCallback((language: string) => setSelectedLocale(language as Locale), [])

	return (
		<Modal id={id} width="small" heading={formatMessage(messages.headline)}>
			<FormLayout>
				{exposeFsOfferEnabled && (
					<Checkbox onChange={setIsSelectedFinancialOffer} checked={isSelectedFinancialOffer}>
						<FormattedMessage {...messages.withFsOffer} />
					</Checkbox>
				)}
				<div>
					<PHeading size="md">
						<FormattedMessage {...messages.languageHeadline} />
					</PHeading>
					<PText>
						<FormattedMessage {...messages.languageSubheadline} />
					</PText>
				</div>
				<ErrorNotificationInline />
				<RadioGroup
					name="language"
					value={selectedLocale}
					onChange={handleLanguageChanged}
					required
					options={languageOptions}
				/>
			</FormLayout>

			<ButtonGroup>
				<PButton loading={isPrinting} onClick={onSubmit}>
					<FormattedMessage {...commonMessages.print} />
				</PButton>
				<PButton type="button" variant="secondary" disabled={isPrinting} onClick={hide}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
			{errorMessage && <StyledValidationMessage message={errorMessage} />}
		</Modal>
	)
})

const StyledValidationMessage = styled(ValidationMessage)`
	margin-top: ${spacingStaticSm};
`
